import { z } from 'zod'
import { CabinClass, CabinCodeEnum, FlightSchema, FlightDateSchema, RbdEnum } from './common'

export const CabinClassResultSchema = z.object({
  price: z.number(),
  seatsAvailable: z.number(),
  cabinCode: CabinCodeEnum,
  rbd: RbdEnum,
})

export type CabinClassResult = z.infer<typeof CabinClassResultSchema>

export const FlightResultSchema = FlightSchema.extend({
  duration: z.number(),
  stops: z.number(),
  peak: z.boolean(),
  departureTerminal: z.string().optional(),
  arrivalTerminal: z.string().optional(),
  cabinClasses: z.object({
    Economy: CabinClassResultSchema.optional(),
    PremiumEconomy: CabinClassResultSchema.optional(),
    Business: CabinClassResultSchema.optional(),
    First: CabinClassResultSchema.optional(),
  }),
})

export type FlightResult = z.infer<typeof FlightResultSchema>

export type CabinName = keyof FlightResult['cabinClasses']

export const cabinNameToCabinClass: Record<CabinName, CabinClass> = {
  Economy: CabinClass.Economy,
  PremiumEconomy: CabinClass.PremiumEconomy,
  Business: CabinClass.Business,
  First: CabinClass.First,
}

export const cabinClassToCabinName: Record<CabinClass, CabinName> = {
  [CabinClass.Economy]: 'Economy',
  [CabinClass.PremiumEconomy]: 'PremiumEconomy',
  [CabinClass.Business]: 'Business',
  [CabinClass.First]: 'First',
}

export const JourneyTypeSchema = z.enum(['inbound', 'outbound'])
export type JourneyType = z.infer<typeof JourneyTypeSchema>

export const JourneyResultSchema = z.object({
  id: z.string(),
  journeyType: JourneyTypeSchema,
  totalDuration: z.number().nullish(),
  direct: z.boolean(),
  departureLocation: z.string(),
  departureTime: z.string(),
  arrivalLocation: z.string(),
  arrivalTime: z.string(),
  stops: z.number(),
  peak: z.boolean(),
  flights: z.array(FlightResultSchema),
  routing: z.string().optional(),
})
export type JourneyResult = z.infer<typeof JourneyResultSchema>

export const FlightAvailabilityResponseSchema = z.object({
  inbound: z.array(JourneyResultSchema),
  outbound: z.array(JourneyResultSchema),
})

export const singleDigitRegexp = /^\d$/

export type FlightAvailabilityResponse = z.infer<typeof FlightAvailabilityResponseSchema>

const airportCodeSchema = z.string().length(3)

export const FlightAvailabilityQuerySchema = z.object({
  adult: z.string().regex(singleDigitRegexp).optional(),
  child: z.string().regex(singleDigitRegexp).optional(),
  childrenAges: z.string().optional(),
  minHoursBeforeDeparture: z.number().optional(),
  departureDate: FlightDateSchema,
  fromAirportCode: airportCodeSchema,
  infant: z.string().regex(singleDigitRegexp).optional(),
  returnDate: FlightDateSchema.optional(),
  toAirportCode: airportCodeSchema,
  youth: z.string().regex(singleDigitRegexp).optional(),
  outboundJourney: z.string().optional(),
  inboundJourney: z.string().optional(),
  outboundCabin: z.string().optional(),
  inboundCabin: z.string().optional(),
})
export type FlightSearchQueryParameters = z.infer<typeof FlightAvailabilityQuerySchema>

export const FlightSearchQuerySchema = z
  .object({
    adult: z.coerce.number().min(1),
    child: z.coerce.number().max(9),
    childrenAges: z.string(),
    departureDate: FlightDateSchema,
    fromAirportCode: z.string().length(3),
    returnDate: FlightDateSchema,
    toAirportCode: z.string().length(3),
    oneWayOnly: z.string(),
  })
  .partial()

export const JourneyCabinSelectionSchema = JourneyResultSchema.extend({
  cabinCode: CabinCodeEnum,
  rbd: RbdEnum,
  price: z.number(),
})

export type JourneyCabinSelection = z.infer<typeof JourneyCabinSelectionSchema>

export const isSameJourney = (journeyOne: JourneyResult, journeyTwo: JourneyResult): boolean => {
  return journeyOne.id === journeyTwo.id
}
